/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { differenceInDays, startOfDay, endOfDay } from 'date-fns';
import {
    Button,
    ButtonGroup,
    Heading,
    InventoryTag,
    FlexRow,
    FlexCol,
    FormattedDateTime,
    TzTimeZone,
} from 'sarsaparilla';
import {
    DATE_FORMAT_LONG_WITH_DAYOFWEEK,
    stringToDate,
} from 'ui-permit/shared-components/utils/dateUtils';
import LotteryImage from './LotteryImage';
import { Lottery } from '../types/lottery';

interface OpenCardProps {
    lottery: Lottery;
}

export default function OpenCard({ lottery }: OpenCardProps) {
    const goEnterLottery = async () => {
        let path;
        switch (lottery.inventory_info.facility_type) {
            case 'permit':
                path = `/permits/${lottery.inventory_id}`;
                break;
            case 'campground':
                path = `/camping/campgrounds/${lottery.inventory_id}`;
                break;
            default:
                path = lottery.inventory_info.enter_url;
        }

        if (path) {
            window.location.assign(path);
        }
    };

    const goViewDetails = () => {
        let path;

        switch (lottery.inventory_info.facility_type) {
            case 'permit':
                path = `/permits/${lottery.inventory_id}`;
                break;
            case 'campground':
                path = `/camping/campgrounds/${lottery.inventory_id}`;
                break;
            default:
                path = lottery.inventory_info.details_url;
        }

        if (path) {
            window.location.assign(path);
        }
    };

    const lotteryInventoryTypeFunc = () => {
        switch (lottery.inventory_info.facility_type) {
            case 'permit':
                return <InventoryTag.InventoryPermit hasText isSpanItem />;
            case 'ticket':
                return <InventoryTag.InventoryTicket hasText isSpanItem />;
            case 'campground':
            case 'camping':
                return <InventoryTag.InventoryCamping hasText isSpanItem />;
            case 'lottery':
                return <InventoryTag.InventoryLottery hasText isSpanItem />;
            default:
                return null;
        }
    };

    const daysDiff = differenceInDays(
        endOfDay(new Date(lottery.close_at)),
        startOfDay(new Date())
    );

    const lotteryDaysOpenRemainingFunc = () => {
        switch (daysDiff) {
            case 1:
            case 2:
                return 'lottery-closing-one-day';
            default:
                return '';
        }
    };

    const lotteryInventoryTypeResult = lotteryInventoryTypeFunc();
    const lotteryDaysOpenRemainingResult = lotteryDaysOpenRemainingFunc();

    //Attempts to grab facility timezone and if not uses UTC for date/time
    let facilityTimezoneWithDefaultUTC = lottery?.inventory_info?.time_zone ?? '';

    if (facilityTimezoneWithDefaultUTC === '') {
        facilityTimezoneWithDefaultUTC = 'UTC';
    }

    return (
        <FlexCol xs={12} className="lottery-open-card-wrap">
            <div className="lottery-card-image-wrap">
                <LotteryImage lottery={lottery} />
            </div>

            <FlexRow className="lottery-card-body-wrap" alignItems="start">
                <FlexCol xs={12} className="lottery-card-top-wrap">
                    {lotteryInventoryTypeResult}

                    <div
                        className={`lottery-closing-tag ${lotteryDaysOpenRemainingResult}`}
                    >
                        Closing in: {daysDiff} {daysDiff === 1 ? 'day' : 'days'}
                    </div>
                </FlexCol>

                <FlexCol md={9} xl={10} className="rec-card-left">
                    <Heading headingLevel={3} appearance="h5" className="mb-half">
                        {lottery.name}
                    </Heading>
                    <div className="lottery-information-wrap">
                        {lottery.inventory_info &&
                            lottery.inventory_info.facility_name && (
                                <div className="lottery-card-info-item">
                                    Part of {lottery.inventory_info.facility_name}
                                </div>
                            )}

                        {lottery.inventory_info && lottery.inventory_info.dates && (
                            <>
                                <div className="lottery-card-title">Event Date</div>
                                <p className="lottery-card-info-item">
                                    <FormattedDateTime
                                        date={stringToDate(
                                            lottery.inventory_info.dates.start
                                        )}
                                        options={{
                                            ...DATE_FORMAT_LONG_WITH_DAYOFWEEK,
                                            timeZone: facilityTimezoneWithDefaultUTC,
                                        }}
                                    />
                                    &nbsp;to&nbsp;
                                    <FormattedDateTime
                                        date={stringToDate(
                                            lottery.inventory_info.dates.end
                                        )}
                                        options={{
                                            ...DATE_FORMAT_LONG_WITH_DAYOFWEEK,
                                            timeZone: facilityTimezoneWithDefaultUTC,
                                        }}
                                    />
                                </p>
                            </>
                        )}

                        <div className="lottery-card-title">Application Deadline</div>
                        <div className="lottery-card-info-item">
                            <FormattedDateTime
                                date={stringToDate(lottery.close_at)}
                                format="llll"
                                timeZone={facilityTimezoneWithDefaultUTC as TzTimeZone}
                                showTimeZone
                            />
                        </div>

                        <div className="lottery-card-title">
                            Lottery Results Available
                        </div>
                        <div className="lottery-card-info-item">
                            <FormattedDateTime
                                date={stringToDate(lottery.announced_at)}
                                options={{
                                    ...DATE_FORMAT_LONG_WITH_DAYOFWEEK,
                                    timeZone: facilityTimezoneWithDefaultUTC,
                                }}
                            />
                        </div>
                    </div>
                </FlexCol>

                <FlexCol md={3} xl={2} alignSelf="stretch" className="lottery-card-right">
                    <ButtonGroup className="lot-stacked-buttons">
                        <Button
                            aria-label={`Enter ${lottery.name}`}
                            shouldFitContainer
                            onClick={goEnterLottery}
                            gaTrackingId="603097493261"
                            gaTrackingValue={lottery.name}
                        >
                            Enter Lottery
                        </Button>
                        {lottery.inventory_info.facility_type !== 'permit' && (
                            <Button
                                aria-label={`View Details for ${lottery.name}`}
                                shouldFitContainer
                                appearance="tertiary"
                                onClick={goViewDetails}
                                gaTrackingId="603097493261"
                                gaTrackingValue={lottery.name}
                            >
                                Show Details
                            </Button>
                        )}
                    </ButtonGroup>
                </FlexCol>
            </FlexRow>
        </FlexCol>
    );
}
